import React, { useEffect, useState } from 'react'
import { FileItem } from '../../model'
import { UploadProduct } from '../../helpers/orderUploads'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'
import Pagination from '../Pagination/Pagination'
import UploadContainer from './UploadContainer'
import UploadProductList from './UploadProductList'

const FILES_PER_PAGE = 10

interface UploadProgress {
    current: number
    total: number
}

interface OrderUploadsProps {
    products: UploadProduct[]
    selectedProductId?: number
    onProductChange(productId?: number, replace?: boolean): void
    files: FileItem[]
    totalFiles: number
    page: number
    onPageChange(page: number): void
    isLoading: boolean
    isUploading: boolean
    progress: UploadProgress
    message: string | null
    onUpload(files: File[]): void | Promise<void>
    onDownload(file: FileItem): void | Promise<void>
    onDelete(file: FileItem): void | Promise<void>
}

const OrderUploads: React.FC<OrderUploadsProps> = ({
    products,
    selectedProductId,
    onProductChange,
    files,
    totalFiles,
    page,
    onPageChange,
    isLoading,
    isUploading,
    progress,
    message,
    onUpload,
    onDownload,
    onDelete
}) => {
    const [width, setWidth] = useState(window.innerWidth)
    const selectedProduct = products.find((product) => product.id === selectedProductId)
    const isMobile = width <= 1024

    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        if (!products.length || selectedProduct || isMobile) return
        onProductChange(products[0].id, true)
    }, [products, selectedProduct, isMobile, onProductChange])

    if (!products.length) {
        return (
            <div className='flex flex-grow items-center justify-center py-8 text-gray-500'>
                Voor deze opdracht zijn geen uploads beschikbaar.
            </div>
        )
    }

    const productList = (
        <UploadProductList products={products} selectedProductId={selectedProductId} onSelect={onProductChange} />
    )

    // On mobile, if no product has been selected, show the product list.
    if (isMobile && !selectedProduct) {
        return (
            <>
                {productList}
                <div className='flex flex-grow items-center justify-center py-8 text-gray-500'>
                    Selecteer een deelopdracht om de uploads te bekijken.
                </div>
            </>
        )
    }

    return (
        <div className='flex flex-grow overflow-hidden rounded-b-xl'>
            {!isMobile && productList}
            <div className='w-full lg:w-2/3 overflow-y-auto py-4 min-w-0'>
                <div className='flex items-center gap-3 px-6 mb-4'>
                    {isMobile && (
                        <button type='button' onClick={() => onProductChange()} aria-label='Terug naar deelopdrachten'>
                            <DynamicFaIcon iconName='arrow-left' className='text-[--tb-primary]' />
                        </button>
                    )}
                    <h3 className='font-semibold'>{selectedProduct?.name}</h3>
                </div>
                <UploadContainer
                    files={files}
                    isLoading={isLoading}
                    isUploading={isUploading}
                    progress={progress}
                    message={message}
                    onUpload={onUpload}
                    onDownload={onDownload}
                    onDelete={onDelete}
                />
                {totalFiles > FILES_PER_PAGE && (
                    <div className='px-2 mt-4'>
                        <Pagination
                            page={page}
                            itemsPerPage={FILES_PER_PAGE}
                            totalItems={totalFiles}
                            onPageChange={onPageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default OrderUploads
